import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import SEO from "@/components/SEO";
import { toast } from "sonner";

const REQUEST_TYPES = [
  { v: "GENERAL",            l: "General issue" },
  { v: "QUOTE_REVIEW",       l: "Quote review" },
  { v: "INSURANCE_GUIDANCE", l: "Insurance guidance" },
  { v: "MAINTENANCE_SUPPORT",l: "Maintenance / repair support" },
  { v: "OTHER",              l: "Other" },
];

const URGENCIES = ["normal", "soon", "urgent"];

export default function RequestSupport() {
  const [form, setForm] = useState({
    request_type: "GENERAL",
    full_name: "", email: "", phone: "",
    park_name: "", plot_number: "", town_location: "",
    urgency: "normal",
    description: "",
  });
  const [uploadIds, setUploadIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/support/request", { ...form, upload_ids: uploadIds });
      setDone(true);
      toast.success("Request received. We'll review and reply.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  }

  if (done) return <Confirmation/>;

  return (
    <div data-testid="request-support-page">
      <SEO
        title="Request Support | Park Home Support"
        description="Submit a support request for quote review, insurance guidance, repair advice or general park home help. Independent specialists."
        canonical="https://parkhomesupport.co.uk/request-support"
      />
      <section className="bg-[#F7F4ED] section-pad">
        <div className="container-phs">
          <span className="eyebrow">Request Support</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl text-[#071A33] leading-[1.05]">
            Tell us what's happening — we'll take it from there.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#334155]">
            Submit one form. We route it to the right specialist and respond with clear, specific next steps.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs max-w-3xl">
          <form className="card-default" onSubmit={onSubmit} data-testid="support-form">

            <Field label="Request type" required>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1" data-testid="req-types">
                {REQUEST_TYPES.map(rt => (
                  <button
                    type="button"
                    key={rt.v}
                    onClick={()=>update("request_type", rt.v)}
                    className={`p-3 text-sm border transition-colors ${form.request_type === rt.v ? "bg-[#064E2E] text-white border-[#064E2E]" : "bg-white text-[#071A33] border-[#E2DCD0] hover:border-[#064E2E]"}`}
                    data-testid={`req-type-${rt.v.toLowerCase()}`}
                  >
                    {rt.l}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              <Field label="Full name" required><input className="field-input" required value={form.full_name} onChange={e=>update("full_name", e.target.value)} data-testid="req-full-name"/></Field>
              <Field label="Email" required><input className="field-input" type="email" required value={form.email} onChange={e=>update("email", e.target.value)} data-testid="req-email"/></Field>
              <Field label="Phone" required><input className="field-input" required value={form.phone} onChange={e=>update("phone", e.target.value)} data-testid="req-phone"/></Field>
              <Field label="Town / location"><input className="field-input" value={form.town_location} onChange={e=>update("town_location", e.target.value)} data-testid="req-town"/></Field>
              <Field label="Park name"><input className="field-input" value={form.park_name} onChange={e=>update("park_name", e.target.value)} data-testid="req-park-name"/></Field>
              <Field label="Plot number"><input className="field-input" value={form.plot_number} onChange={e=>update("plot_number", e.target.value)} data-testid="req-plot-number"/></Field>
              <Field label="Urgency">
                <select className="field-input" value={form.urgency} onChange={e=>update("urgency", e.target.value)} data-testid="req-urgency">
                  {URGENCIES.map(u => <option key={u} value={u}>{u.charAt(0).toUpperCase()+u.slice(1)}</option>)}
                </select>
              </Field>
            </div>

            <div className="mt-6">
              <label className="field-label">Describe the issue <span className="text-[#B88A2A]">*</span></label>
              <textarea className="field-input" required value={form.description} onChange={e=>update("description", e.target.value)} data-testid="req-description"/>
            </div>

            <div className="mt-6">
              <FileUpload label="Photos, videos, reports or quotes" testId="req-upload" onChange={setUploadIds}/>
            </div>

            <button disabled={submitting} className="btn-primary mt-7" data-testid="req-submit-btn">
              {submitting ? <><Loader2 className="animate-spin" size={16}/> Sending…</> : <>Send request <ArrowRight size={16}/></>}
            </button>
            <p className="mt-3 text-xs text-[#475569]">By submitting you start the service. <Link to="/terms" className="link-underline">Terms apply</Link>.</p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required ? <span className="text-[#B88A2A]"> *</span> : null}</label>
      {children}
    </div>
  );
}

function Confirmation() {
  return (
    <section className="bg-[#F7F4ED] section-pad">
      <div className="container-phs max-w-2xl">
        <div className="card-default text-center" data-testid="req-confirmation">
          <h1 className="font-display text-3xl text-[#071A33]">Request received</h1>
          <p className="mt-3 text-[#334155]">A specialist will review and respond to your email shortly.</p>
          <Link to="/" className="btn-outline mt-6 inline-flex">Back to Home</Link>
        </div>
      </div>
    </section>
  );
}
